import BreadcrumbComponent from '@/components/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLogs } from '@/hooks/useLogs';
import { formatDate } from '@/lib/utils';
import { Edit3, Eye, Loader2, PlusCircle, Trash2 } from 'lucide-react';

export default function LogsPage() {
  const { logs, loading, hasMore, loadMore } = useLogs(10);

  const getTypeConfig = (type) => {
    switch (type) {
      case 'Created':
        return {
          color:
            'bg-green-100 dark:bg-green-600 text-green-700 dark:text-white',
          icon: <PlusCircle className="w-4 h-4 mr-1" />,
        };
      case 'Edited':
        return {
          color: 'bg-blue-100 dark:bg-blue-600 text-blue-700 dark:text-white',
          icon: <Edit3 className="w-4 h-4 mr-1" />,
        };
      case 'Deleted':
        return {
          color: 'bg-red-100 dark:bg-red-600 text-red-700 dark:text-white',
          icon: <Trash2 className="w-4 h-4 mr-1" />,
        };
      default:
        return {
          color: 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-white',
          icon: null,
        };
    }
  };

  return (
    <div className="px-4 lg:px-8 my-4 space-y-4">
      <BreadcrumbComponent title="Harakatlar tarixi" />

      <div>
        <h2 className="text-2xl font-bold tracking-tight">
          Admin harakatlari tarixi
        </h2>
        <p className="text-muted-foreground">
          Foydalanuvchi faoliyati loglarini ko'rib chiqing va boshqaring.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableCaption className="hidden">
            Foydalanuvchi faoliyati loglari
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Turi</TableHead>
              <TableHead>Kontekst</TableHead>
              <TableHead>Harakat</TableHead>
              <TableHead>Sana / Vaqt</TableHead>
              <TableHead>Detal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-gray-500">
                  Loglar yuklanmoqda...
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-gray-500">
                  Loglar mavjud emas
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const { color, icon } = getTypeConfig(log.type);

                return (
                  <TableRow key={log.id}>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${color}`}
                      >
                        {icon}
                        {log.type}
                      </span>
                    </TableCell>
                    <TableCell>{log.context}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {log.timestamp ? formatDate(log.timestamp, true) : '-'}
                    </TableCell>
                    <TableCell>
                      {log.details ? (
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Eye className="w-5 h-5 text-gray-600" />
                            </Button>
                          </SheetTrigger>
                          <SheetContent
                            side="right"
                            className="w-[400px] sm:w-[500px]"
                          >
                            <SheetHeader>
                              <SheetTitle>Log tafsilotlari</SheetTitle>
                            </SheetHeader>
                            <div className="mt-4 space-y-2 text-sm">
                              <pre className="bg-muted p-3 rounded-md overflow-auto text-xs">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </div>
                          </SheetContent>
                        </Sheet>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {hasMore !== null && hasMore && (
        <div className="flex justify-center mt-4">
          <Button onClick={loadMore} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Ko‘proq yuklash
          </Button>
        </div>
      )}
    </div>
  );
}
